import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ShieldCheck,
  MapPin,
  Phone,
  Camera,
  X,
  Package,
  Clock,
} from 'lucide-react';
import { deliveryService, type DeliveryIncident, type DeliveryIncidentProof } from '@services/delivery.service';
import { ForceEndTripModal } from '@components/delivery/ForceEndTripModal';
import { PageGuard } from '@components/PageGuard';
import { cn, formatDate } from '@lib/utils';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Say exactly what the coordinates support, and nothing more. */
function distanceLine(incident: DeliveryIncident): string {
  if (incident.distanceM == null) {
    return 'No location was recorded with this report, so the distance from the drop-off is unknown.';
  }
  const d = incident.distanceM;
  const reading = d >= 1000 ? `${(d / 1000).toFixed(1)} km` : `${d} m`;
  const source =
    incident.riderLocation?.source === 'proof_photo'
      ? 'measured from where the proof photo was taken'
      : incident.riderLocation?.source === 'report_event'
        ? 'measured from where the report was filed'
        : 'measured from the last known rider location';
  return `Rider was ${reading} from the drop-off, ${source}.`;
}

const STATUS_CHIP: Record<string, string> = {
  disputed: 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
  delivered: 'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  otp_confirmed: 'bg-[var(--state-success-bg)] text-[var(--state-success)]',
  cancelled: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
  failed: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
};

function StatusChip({ status }: { status: string }) {
  return (
    <span
      className={cn(
        'rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
        STATUS_CHIP[status] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
      )}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
      {children}
    </p>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function IncidentSkeleton() {
  return (
    <div className="rounded-3xl bg-white shadow-soft p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="h-3 w-24 rounded-full bg-[var(--bg-elevated)] animate-pulse" />
          <div className="h-5 w-56 rounded-xl bg-[var(--bg-elevated)] animate-pulse" />
        </div>
        <div className="h-6 w-20 rounded-full bg-[var(--bg-elevated)] animate-pulse" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-2">
            <div className="h-3 w-20 rounded-full bg-[var(--bg-elevated)] animate-pulse" />
            <div className="h-4 w-32 rounded-xl bg-[var(--bg-elevated)] animate-pulse" />
          </div>
        ))}
      </div>
      <div className="h-16 rounded-2xl bg-[var(--bg-elevated)] animate-pulse" />
      <div className="flex gap-3">
        <div className="h-20 w-20 rounded-2xl bg-[var(--bg-elevated)] animate-pulse" />
        <div className="h-20 w-20 rounded-2xl bg-[var(--bg-elevated)] animate-pulse" />
      </div>
    </div>
  );
}

// ── Proof lightbox ────────────────────────────────────────────────────────────

function ProofLightbox({ proof, onClose }: { proof: DeliveryIncidentProof; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-60 flex items-center justify-center p-6 bg-[rgba(15,61,30,0.55)]"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-lift max-w-3xl w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)]">
          <div>
            <Label>Proof photo</Label>
            <p className="text-[14px] font-semibold text-[var(--text-primary)] capitalize mt-0.5">
              {proof.kind.replace(/_/g, ' ')} · {formatDate(proof.createdAt, 'long')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)]"
            aria-label="Close photo"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="bg-[var(--bg-base)] flex items-center justify-center max-h-[70vh]">
          <img
            src={proof.photoUrl}
            alt="Delivery proof"
            className="max-h-[70vh] w-auto object-contain"
          />
        </div>
        {proof.latitude != null && proof.longitude != null && (
          <div className="px-6 py-3 text-[12px] text-[var(--text-muted)] flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
            {proof.latitude.toFixed(5)}, {proof.longitude.toFixed(5)}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Incident card ─────────────────────────────────────────────────────────────

interface IncidentCardProps {
  incident: DeliveryIncident;
  onResolve: () => void;
  onOpenProof: (proof: DeliveryIncidentProof) => void;
}

function IncidentCard({ incident, onResolve, onOpenProof }: IncidentCardProps) {
  const orderLabel = incident.orderNumber != null
    ? `Order #${incident.orderNumber}`
    : incident.orderId
      ? `Order ${incident.orderId.slice(0, 8)}`
      : 'Order unknown';

  return (
    <div className="rounded-3xl bg-white shadow-soft p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <Label>Delivery incident</Label>
          <div className="flex items-center gap-2 flex-wrap">
            {incident.orderId ? (
              <Link
                to={`/orders/${incident.orderId}`}
                className="text-[17px] font-semibold text-[var(--text-primary)] hover:underline"
              >
                {orderLabel}
              </Link>
            ) : (
              <span className="text-[17px] font-semibold text-[var(--text-primary)]">{orderLabel}</span>
            )}
            {incident.businessName && (
              <span className="text-[13px] text-[var(--text-muted)]">{incident.businessName}</span>
            )}
          </div>
          <p className="text-[12px] text-[var(--text-muted)] font-mono">
            {incident.deliveryRef ? `${incident.deliveryRef} · ` : ''}
            {incident.assignmentId}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusChip status={incident.status} />
          <span className="flex items-center gap-1.5 text-[12px] text-[var(--text-muted)]">
            <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
            {formatDate(incident.raisedAt, 'relative')}
          </span>
        </div>
      </div>

      {/* Facts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-1">
          <Label>Rider</Label>
          {incident.rider ? (
            <>
              <Link
                to={`/riders/${incident.rider.id}`}
                className="block text-[14px] font-medium text-[var(--text-primary)] hover:underline"
              >
                {incident.rider.name ?? 'Unnamed rider'}
              </Link>
              {incident.rider.phone && (
                <a
                  href={`tel:${incident.rider.phone}`}
                  className="flex items-center gap-1.5 text-[13px] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                >
                  <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {incident.rider.phone}
                </a>
              )}
            </>
          ) : (
            <p className="text-[14px] text-[var(--text-muted)]">No rider attached</p>
          )}
        </div>

        <div className="space-y-1">
          <Label>Drop-off</Label>
          <p className="text-[14px] text-[var(--text-primary)]">
            {incident.dropoff.address ?? 'No address on the order'}
          </p>
          {incident.dropoff.instructions && (
            <p className="text-[13px] text-[var(--text-muted)]">{incident.dropoff.instructions}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label>Customer</Label>
          <p className="text-[14px] text-[var(--text-primary)]">
            {incident.customer?.name ?? 'Not recorded'}
          </p>
          {incident.customer?.phone && (
            <a
              href={`tel:${incident.customer.phone}`}
              className="flex items-center gap-1.5 text-[13px] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            >
              <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />
              {incident.customer.phone}
            </a>
          )}
        </div>
      </div>

      {/* The rider's own words */}
      <div className="rounded-2xl bg-[var(--bg-base)] border border-[var(--border-subtle)] px-5 py-4 space-y-2">
        <Label>What the rider reported</Label>
        <p className="text-[14px] text-[var(--text-primary)] leading-relaxed">
          {incident.riderNote?.trim()
            ? `“${incident.riderNote.trim()}”`
            : 'The rider left no note with this report.'}
        </p>
        {incident.reasonText && (
          <p className="text-[13px] text-[var(--text-muted)]">{incident.reasonText}</p>
        )}
        {incident.receivedBy && (
          <p className="text-[13px] text-[var(--text-muted)]">
            Handed to: {incident.receivedBy.replace(/_/g, ' ')}
          </p>
        )}
        <p className="flex items-start gap-1.5 text-[13px] text-[var(--text-accent)]">
          <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={1.5} />
          {distanceLine(incident)}
        </p>
      </div>

      {/* Evidence */}
      <div className="space-y-2">
        <Label>
          {incident.proofs.length > 0
            ? `Proof photos (${incident.proofs.length})`
            : 'Proof photos'}
        </Label>
        {incident.proofs.length === 0 ? (
          <p className="text-[13px] text-[var(--text-muted)]">No photo was attached to this report.</p>
        ) : (
          <div className="flex gap-3 flex-wrap">
            {incident.proofs.map((proof) => (
              <button
                key={proof.id}
                onClick={() => onOpenProof(proof)}
                className="group relative w-20 h-20 rounded-2xl overflow-hidden border border-[var(--border-subtle)] hover:border-[var(--border-strong)] transition-colors"
                title={`${proof.kind} photo, ${formatDate(proof.createdAt, 'short')}`}
              >
                <img
                  src={proof.photoUrl}
                  alt="Delivery proof thumbnail"
                  className="w-full h-full object-cover"
                />
                <span className="absolute inset-0 flex items-center justify-center bg-[rgba(15,61,30,0.45)] opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera className="w-4 h-4 text-white" strokeWidth={1.5} />
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Decision */}
      <div className="flex items-center justify-between gap-4 pt-1 flex-wrap">
        <p className="text-[13px] text-[var(--text-muted)]">
          This trip stays open until support ends it.
        </p>
        <button
          onClick={onResolve}
          className="rounded-full border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-5 py-2.5 text-[13px] font-medium text-[var(--state-danger)] hover:border-[var(--state-danger)]"
        >
          Resolve trip
        </button>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const DeliveryIncidentsPage = () => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'open' | 'all'>('open');
  const [resolving, setResolving] = useState<DeliveryIncident | null>(null);
  const [lightbox, setLightbox] = useState<DeliveryIncidentProof | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['delivery', 'incidents', status],
    queryFn: () => deliveryService.listDeliveryIncidents({ status, limit: 50 }),
    refetchInterval: 60000,
  });

  const incidents = data?.incidents ?? [];

  const refetchAll = () => {
    queryClient.invalidateQueries({ queryKey: ['delivery', 'incidents'] });
  };

  return (
    <PageGuard permission="settings.read">
      <div className="min-h-screen bg-[var(--bg-base)] p-8 space-y-6">
        {/* Page header */}
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
              Delivery
            </p>
            <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
              Delivery incidents
            </h1>
            <p className="text-[14px] text-[var(--text-muted)]">
              Trips a rider reported as undeliverable. Each one waits here until support ends it.
            </p>
          </div>

          <div className="flex items-center gap-1 rounded-2xl bg-white shadow-soft p-1">
            {(['open', 'all'] as const).map((value) => (
              <button
                key={value}
                onClick={() => setStatus(value)}
                className={cn(
                  'rounded-xl px-4 py-2 text-[13px] font-medium transition-colors',
                  status === value
                    ? 'bg-[var(--brand-forest)] text-white'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]',
                )}
              >
                {value === 'open' ? 'Open' : 'All'}
              </button>
            ))}
          </div>
        </div>

        {/* Count bar */}
        {!isLoading && incidents.length > 0 && (
          <div className="flex items-center gap-2 rounded-2xl bg-[var(--state-warning-bg)] border border-[var(--state-warning-border)] px-4 py-2.5 w-fit">
            <AlertTriangle className="w-4 h-4 text-[var(--state-warning)]" strokeWidth={1.5} />
            <span className="text-[13px] font-semibold text-[var(--state-warning)]">
              {data?.total ?? incidents.length} {status === 'open' ? 'awaiting a decision' : 'incidents on record'}
            </span>
          </div>
        )}

        {/* Queue */}
        {isLoading ? (
          <div className="space-y-4">
            <IncidentSkeleton />
            <IncidentSkeleton />
            <IncidentSkeleton />
          </div>
        ) : incidents.length === 0 ? (
          <div className="rounded-3xl bg-white shadow-soft py-20 flex flex-col items-center justify-center text-center px-6">
            <div className="w-14 h-14 rounded-2xl bg-[var(--bg-elevated)] flex items-center justify-center mb-4">
              <ShieldCheck className="w-6 h-6 text-[var(--text-accent)]" strokeWidth={1.5} />
            </div>
            <h2 className="text-[17px] font-semibold text-[var(--text-primary)]">
              {status === 'open' ? 'No open delivery incidents' : 'No delivery incidents on record'}
            </h2>
            <p className="text-[13px] text-[var(--text-muted)] mt-1 max-w-sm">
              Every reported delivery has been dealt with. New reports land here the moment a rider files one.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {incidents.map((incident) => (
              <IncidentCard
                key={incident.assignmentId}
                incident={incident}
                onResolve={() => setResolving(incident)}
                onOpenProof={setLightbox}
              />
            ))}
          </div>
        )}

        {/* Footnote: what this page cannot do */}
        {!isLoading && incidents.length > 0 && (
          <p className="flex items-center gap-1.5 text-[12px] text-[var(--text-muted)]">
            <Package className="w-3.5 h-3.5" strokeWidth={1.5} />
            Ending a trip here records the outcome on the order. Refunds beyond the chosen outcome are handled in Disputes.
          </p>
        )}
      </div>

      <ForceEndTripModal
        open={!!resolving}
        onClose={() => setResolving(null)}
        assignmentId={resolving?.assignmentId}
        subject={
          resolving
            ? `${resolving.orderNumber != null ? `Order #${resolving.orderNumber}` : 'Order'} · ${resolving.rider?.name ?? 'no rider'}`
            : undefined
        }
        onDone={refetchAll}
      />

      {lightbox && <ProofLightbox proof={lightbox} onClose={() => setLightbox(null)} />}
    </PageGuard>
  );
};
